import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  MessageSquare, 
  Check, 
  Loader2, 
  CreditCard,
  Sparkles,
  Shield,
  Users,
  Mail,
  BarChart3,
  Palette,
  Zap,
  AlertCircle,
  HelpCircle,
  Phone,
  CreditCard as CardIcon
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUPPORT_EMAIL = 'hello@intakepal.com';

// LinkedIn Conversion Tracking Helper
const trackLinkedInConversion = () => {
  if (typeof window !== 'undefined' && window.lintrk) {
    window.lintrk('track', { conversion_id: 28515713 });
  }
};

const PLAN_FEATURES = [
  { icon: Zap, text: 'Unlimited lead capture' },
  { icon: Sparkles, text: 'Automated case summaries' },
  { icon: Mail, text: 'Automated email/SMS follow-ups' },
  { icon: Users, text: 'Team member accounts' },
  { icon: Palette, text: 'Custom branding & widget' },
  { icon: BarChart3, text: 'Analytics dashboard' },
  { icon: Shield, text: 'Lead scoring & prioritization' },
];

// Card Tips Component - shown before checkout
const CardTipsSection = () => (
  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
    <div className="flex items-start gap-3">
      <CardIcon className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
      <div className="text-sm">
        <p className="font-medium text-blue-800 mb-1">Payment Tips</p>
        <p className="text-blue-700">
          If your card is declined, don&apos;t worry — some banks flag first-time purchases with new merchants. 
          Try another card or contact your bank to approve the transaction.
        </p>
      </div>
    </div>
  </div>
);

// Payment Help Modal/Section for declined cards
const PaymentHelpSection = ({ onClose }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-6">
    <div className="flex items-start gap-3">
      <AlertCircle className="w-6 h-6 text-amber-500 mt-0.5 flex-shrink-0" />
      <div>
        <h4 className="font-semibold text-amber-800 mb-2">Card Declined? Here&apos;s What To Do:</h4>
        <ul className="text-sm text-amber-700 space-y-2 mb-4">
          <li className="flex items-start gap-2">
            <span className="font-medium">1.</span>
            <span><strong>Try a different card</strong> — Another credit or debit card may work better</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-medium">2.</span>
            <span><strong>Contact your bank</strong> — Ask them to approve the transaction with IntakePal</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-medium">3.</span>
            <span><strong>Use Google Pay or Apple Pay</strong> — These often bypass bank fraud checks</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="font-medium">4.</span>
            <span><strong>Need help?</strong> — We&apos;re here for you!</span>
          </li>
        </ul>
        <div className="flex flex-wrap gap-3">
          <a 
            href={`mailto:${SUPPORT_EMAIL}?subject=Payment%20Issue%20-%20Card%20Declined&body=Hi%20IntakePal%20Team%2C%0A%0AI'm%20having%20trouble%20completing%20my%20subscription%20payment.%20My%20card%20was%20declined.%0A%0APlease%20help!`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg text-sm font-medium transition-colors"
          >
            <Mail className="w-4 h-4" />
            Email Support
          </a>
          <button
            onClick={onClose}
            className="px-4 py-2 text-amber-700 hover:text-amber-900 text-sm font-medium"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  </div>
);

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [showPaymentHelp, setShowPaymentHelp] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`);
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkPaymentStatus = useCallback(async (sessionId) => {
    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/subscription/check/${sessionId}`);
      
      if (response.data.status === 'success') {
        toast.success(response.data.message);
        setSubscription({
          status: 'trialing',
          plan: 'pro',
          trial_ends_at: response.data.trial_ends_at
        });
        // Redirect to dashboard after successful subscription
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        toast.error('Payment not completed. Please try again.');
        setShowPaymentHelp(true);
      }
    } catch (error) {
      console.error('Payment check error:', error);
      toast.error('Failed to verify payment');
      setShowPaymentHelp(true);
    } finally {
      setCheckingPayment(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchSubscription();
    
    // Check if returning from Stripe checkout
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
    
    // Check if returning from cancelled/failed checkout
    const cancelled = searchParams.get('cancelled');
    if (cancelled) {
      setShowPaymentHelp(true);
    }
  }, [fetchSubscription, checkPaymentStatus, searchParams]);

  const handleSubscribe = async () => {
    setSubscribing(true);
    setCheckoutError(null);
    trackLinkedInConversion(); // Track subscription click
    try {
      const response = await axios.post(`${API}/subscription/create-checkout`);
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Subscription error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to start checkout. Please try again.';
      setCheckoutError(errorMessage);
      toast.error(errorMessage);
      setShowPaymentHelp(true);
      setSubscribing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isActive = subscription?.status === 'active' || subscription?.status === 'trialing';

  if (loading || checkingPayment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-teal-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">
            {checkingPayment ? 'Verifying your payment...' : 'Loading subscription...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                IntakePal
              </span>
            </div>
            {isActive && (
              <Button 
                variant="outline"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Active Subscription */}
        {isActive ? (
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-4">
              <Check className="w-4 h-4" />
              {subscription.status === 'trialing' ? 'Free Trial Active' : 'Subscription Active'}
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              You&apos;re all set, {user?.full_name}!
            </h1>
            {subscription.status === 'trialing' && subscription.trial_ends_at && (
              <p className="text-slate-600">
                Your free trial ends on <strong>{formatDate(subscription.trial_ends_at)}</strong>
              </p>
            )}
            <Button 
              className="mt-6 bg-teal-500 hover:bg-teal-600"
              onClick={() => navigate('/dashboard')}
            >
              Continue to Dashboard
            </Button>
            <Button 
              variant="outline"
              className="mt-3 ml-3"
              onClick={() => navigate('/subscription/manage')}
              data-testid="manage-subscription-btn"
            >
              Manage Subscription
            </Button>
          </div>
        ) : (
          <>
            {/* Paywall Header */}
            <div className="text-center mb-10">
              <h1 
                className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Start Your 7-Day Free Trial
              </h1>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Get full access to IntakePal&apos;s powerful intake automation. 
                No charge until your trial ends.
              </p>
            </div>

            {/* Synthesia Video */}
            <div className="max-w-2xl mx-auto mb-10">
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-4">
                <div style={{ position: 'relative', overflow: 'hidden', aspectRatio: '1920/1080', borderRadius: '0.5rem' }}>
                  <iframe 
                    src="https://share.synthesia.io/embeds/videos/915d89e7-31c2-43cb-858c-dec16baba160" 
                    loading="lazy" 
                    title="Synthesia video player - Stop Losing Injury Leads" 
                    allowFullScreen 
                    allow="encrypted-media; fullscreen; microphone; screen-wake-lock;" 
                    style={{ position: 'absolute', width: '100%', height: '100%', top: 0, left: 0, border: 'none', padding: 0, margin: 0, overflow: 'hidden' }}
                  />
                </div>
              </div>
              <p className="text-center text-sm text-slate-500 mt-3">
                See how IntakePal helps law firms convert more leads
              </p>
            </div>

            {/* Pricing Card */}
            <Card className="max-w-lg mx-auto border-2 border-teal-500 shadow-xl">
              <CardHeader className="text-center pb-2 bg-gradient-to-br from-teal-500 to-emerald-500 text-white rounded-t-lg">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 rounded-full text-sm font-medium mb-4 mx-auto">
                  <Sparkles className="w-4 h-4" />
                  Most Popular
                </div>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  IntakePal Pro
                </CardTitle>
                <CardDescription className="text-teal-100">
                  Everything you need to automate client intake
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Payment Help Section - shown when there's an error */}
                {showPaymentHelp && (
                  <PaymentHelpSection onClose={() => setShowPaymentHelp(false)} />
                )}

                {/* Price */}
                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-slate-900">$179.99</span>
                    <span className="text-slate-500">/month</span>
                  </div>
                  <p className="text-sm text-teal-600 font-medium mt-2">
                    7-day free trial included
                  </p>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-8">
                  {PLAN_FEATURES.map((feature) => (
                    <div key={feature.text} className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center">
                        <feature.icon className="w-4 h-4 text-teal-600" />
                      </div>
                      <span className="text-slate-700">{feature.text}</span>
                    </div>
                  ))}
                </div>

                {/* Card Tips - shown before checkout */}
                <CardTipsSection />

                {/* CTA Button */}
                <Button
                  onClick={handleSubscribe}
                  disabled={subscribing}
                  className="w-full bg-teal-500 hover:bg-teal-600 text-lg py-6"
                  data-testid="subscribe-btn"
                >
                  {subscribing ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-5 h-5 mr-2" />
                  )}
                  Start Free Trial
                </Button>

                <p className="text-center text-xs text-slate-500 mt-4">
                  Cancel anytime. You won&apos;t be charged until after your 7-day trial.
                </p>

                {/* Need Help Link */}
                <div className="text-center mt-4 pt-4 border-t border-slate-100">
                  <a 
                    href={`mailto:${SUPPORT_EMAIL}?subject=Subscription%20Question`}
                    className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-teal-600 transition-colors"
                  >
                    <HelpCircle className="w-4 h-4" />
                    Need help? Contact us at {SUPPORT_EMAIL}
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Trust Badges */}
            <div className="flex items-center justify-center gap-8 mt-12 text-slate-400">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                <span className="text-sm">Secure Payment</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                <span className="text-sm">Cancel Anytime</span>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default SubscriptionPage;
